import { useMemo } from "react";

import { ParsedGuardrails } from "../../flows/StockCorrections/hooks/useGuardrailsFeatureFlag";

export type UseGuardrailValidationParams = {
  expectedValue: number;
  countedValue: number;
  productPrice: number;
  guardrails?: ParsedGuardrails;
};

export type UseGuardrailValidationResult = {
  gap: number;
  quantityExceeded: boolean;
  valueExceeded: boolean;
  isOverLimit: boolean;
};

export function useGuardrailValidation({
  expectedValue,
  countedValue,
  productPrice,
  guardrails,
}: UseGuardrailValidationParams): UseGuardrailValidationResult {
  const gap = useMemo(() => {
    if (!expectedValue) return 0;
    return Math.abs(countedValue - expectedValue);
  }, [countedValue, expectedValue]);

  const quantityExceeded = useMemo(() => {
    if (!guardrails?.quantityThreshold) return false;
    return gap >= guardrails.quantityThreshold;
  }, [gap, guardrails?.quantityThreshold]);

  const valueExceeded = useMemo(() => {
    if (!guardrails?.valueThresholds) return false;
    return gap * productPrice >= guardrails.valueThresholds;
  }, [gap, guardrails?.valueThresholds, productPrice]);

  const isOverLimit = useMemo(() => {
    if (guardrails?.quantityChangeLimit === undefined) return false;
    return gap > guardrails.quantityChangeLimit;
  }, [gap, guardrails?.quantityChangeLimit]);

  return {
    gap,
    quantityExceeded,
    valueExceeded,
    isOverLimit,
  };
}
